import { apiGet, apiRequest } from "./client.js";
import { parseFeedHealth, parseHealthPolicy } from "./parse.js";
import type { FeedHealth, HealthPolicy } from "./types.js";

/**
 * What the product established about one feed by consuming what that feed publishes,
 * and the tolerances it judged that feed against. Both are per feed and neither is
 * published to a consumer: the outward seam carries three states and no detail.
 */

const healthPath = (feedId: string, suffix: string) =>
  `/api/feeds/${encodeURIComponent(feedId)}/${suffix}`;

export async function fetchFeedHealth(
  feedId: string,
  signal?: AbortSignal,
): Promise<FeedHealth> {
  return parseFeedHealth(
    await apiGet<unknown>(healthPath(feedId, "health"), { signal }),
  );
}

export async function fetchHealthPolicy(
  feedId: string,
  signal?: AbortSignal,
): Promise<HealthPolicy> {
  return parseHealthPolicy(
    await apiGet<unknown>(healthPath(feedId, "health-policy"), { signal }),
  );
}

/**
 * The published patch schema, field for field. `bitrate_tolerance_percent` is on the
 * row and in the response but not in `FeedHealthPolicyPatchSchema`, so it is not
 * here either — this surface edits what the API accepts and invents nothing.
 */
export interface HealthPolicyInput {
  enabled?: boolean;
  check_interval_seconds?: number;
  sample_seconds?: number;
  connect_timeout_ms?: number;
  silence_threshold_dbfs?: number;
  silence_tolerance_seconds?: number;
  fallback_tolerance_seconds?: number;
  loudness_tolerance_lu?: number;
  true_peak_tolerance_db?: number;
  consecutive_failures_to_alert?: number;
}

export async function patchHealthPolicy(
  feedId: string,
  input: HealthPolicyInput,
): Promise<HealthPolicy> {
  return parseHealthPolicy(
    await apiRequest<unknown>(healthPath(feedId, "health-policy"), {
      method: "PATCH",
      body: input,
    }),
  );
}
